import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ScanLine, Camera, Images, Loader2, UserPlus, Target, AlertTriangle, ExternalLink, RotateCcw, Link2, Pencil, X, Layers, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDropdowns } from "@/hooks/useMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UniverseLinkInput } from "@/components/UniverseLinkInput";
import { ContactDialog } from "@/components/ContactDialog";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { PhoneInput } from "@/components/PhoneInput";
import { withDefaultCode } from "@/lib/countries";

export const SCAN_ROLES = ["super_admin", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc", "marketing"];
const MAX_SIDE = 1400;

export function downscale(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

function F({ label, k, f, set, testid, type = "text" }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <Input type={type} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9" data-testid={testid} />
    </div>
  );
}

function CaptureButtons({ onCamera, onGallery, busy, small }) {
  const cls = small
    ? "h-10 rounded-md border border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 flex items-center justify-center gap-2 text-xs text-slate-700 transition-colors"
    : "h-28 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 flex flex-col items-center justify-center gap-2 text-sm text-slate-700 transition-colors";
  return (
    <div className="grid grid-cols-2 gap-3">
      <button type="button" onClick={onCamera} disabled={busy} className={cls} data-testid="scan-take-photo-btn">
        <Camera className={small ? "h-4 w-4 text-brand" : "h-6 w-6 text-brand"} /> {small ? "Scan next card" : "Take photo"}
      </button>
      <button type="button" onClick={onGallery} disabled={busy} className={cls} data-testid="scan-pick-photo-btn">
        <Images className={small ? "h-4 w-4 text-slate-500" : "h-6 w-6 text-slate-500"} /> {small ? "From gallery" : "Choose from gallery"}
      </button>
    </div>
  );
}

function QueueItem({ item, idx, onChange, onRemove }) {
  const [edit, setEdit] = useState(false);
  const set = (k, v) => onChange({ ...item, [k]: v });
  return (
    <li className={`rounded-md border p-2 text-xs ${item.dup ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-white"}`} data-testid={`batch-item-${idx}`}>
      <div className="flex items-start gap-2">
        <span className="h-5 w-5 shrink-0 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 truncate">{item.name || <span className="text-amber-600 italic">(no name — fix before saving)</span>}</p>
          <p className="text-slate-500 truncate">{[item.designation, item.company].filter(Boolean).join(" · ") || "—"}</p>
          <p className="text-slate-500 truncate">{[item.phone, item.email].filter(Boolean).join(" · ") || "—"}</p>
          {item.dup && <p className="text-amber-700 mt-0.5 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Already in CRM as {item.dupName} — will be skipped</p>}
          {item.universe_id && <p className="text-emerald-700 mt-0.5 inline-flex items-center gap-1"><Link2 className="h-3 w-3" /> Universe: {item.universe_company}</p>}
        </div>
        <button type="button" onClick={() => setEdit(!edit)} className="text-slate-400 hover:text-slate-800" title="Edit" data-testid={`batch-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={onRemove} className="text-slate-400 hover:text-red-600" title="Remove" data-testid={`batch-remove-${idx}`}><X className="h-3.5 w-3.5" /></button>
      </div>
      {edit && (
        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-100">
          <Input value={item.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Name" className="h-8 text-xs col-span-2" data-testid={`batch-name-${idx}`} />
          <Input value={item.designation || ""} onChange={(e) => set("designation", e.target.value)} placeholder="Designation" className="h-8 text-xs" />
          <Input value={item.company || ""} onChange={(e) => onChange({ ...item, company: e.target.value, universe_id: null, universe_company: "" })} placeholder="Company" className="h-8 text-xs" />
          <PhoneInput value={item.phone || ""} onChange={(v) => set("phone", v)} className="h-8 text-xs" testid={`batch-phone-${idx}`} warnNoCode />
          <Input value={item.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="h-8 text-xs" />
        </div>
      )}
    </li>
  );
}

export function ScanCardDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const { data: dd } = useDropdowns();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [f, setFields] = useState({});
  const [eventName, setEventName] = useState(() => sessionStorage.getItem("scan_event_name") || "");
  const [batch, setBatch] = useState(() => sessionStorage.getItem("scan_batch_mode") === "1");
  const [queue, setQueue] = useState([]);
  const [batchType, setBatchType] = useState("Customer");
  const [saving, setSaving] = useState(false);
  const [contactInit, setContactInit] = useState(null);
  const [leadInit, setLeadInit] = useState(null);
  const set = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const isGlobal = user?.role === "super_admin" || user?.role === "management";

  const reset = () => { setPreview(null); setScan(null); setFields({}); };
  const close = (v) => { if (!v) { reset(); if (!batch) setQueue([]); } onOpenChange(v); };
  const toggleBatch = (v) => { setBatch(v); sessionStorage.setItem("scan_batch_mode", v ? "1" : "0"); reset(); };
  const rememberEvent = () => sessionStorage.setItem("scan_event_name", eventName.trim());

  const fieldsFrom = (data) => {
    const x = data.fields, exact = data.universe_exact;
    return { name: x.name, designation: x.designation, company: exact?.company || x.company, phone: x.phone, email: x.email,
      website: x.website, city: x.city, address: x.address, phones: x.phones,
      universe_id: exact?.id || null, universe_company: exact?.company || "" };
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)/i.test(file.type) && !/\.(jpe?g|png|webp|heic)$/i.test(file.name)) { toast.error("Please pick a JPEG/PNG photo"); return; }
    setBusy(true);
    rememberEvent();
    try {
      const dataUrl = await downscale(file);
      setPreview(dataUrl);
      const { data } = await api.post("/card-scan/extract", { image_base64: dataUrl, event_name: eventName });
      const x = fieldsFrom(data);
      const empty = !x.name && !x.company && !x.phone && !x.email;
      if (batch) {
        if (empty) { toast.warning("Couldn't read that card — try again closer and sharper."); setPreview(null); return; }
        const dupC = data.duplicates?.contacts?.[0];
        setQueue((q) => [...q, { ...x, scan_id: data.scan_id, dup: !!dupC, dupName: dupC?.name }]);
        toast.success(`Added ${x.name || x.company} · ${queue.length + 1} in batch`);
        setPreview(null);
      } else {
        setScan(data);
        setFields(x);
        if (empty) toast.warning("Couldn't read any details — try a sharper, closer photo.");
      }
    } catch (err) { toast.error(apiError(err)); setPreview(null); }
    finally { setBusy(false); }
  };

  const base = () => ({
    name: f.name, company: f.company, designation: f.designation, phone: withDefaultCode(f.phone), email: f.email,
    universe_id: f.universe_id || null, universe_company: f.universe_company || "",
    card_scan_id: scan?.scan_id, event_name: eventName.trim(),
  });

  const asContact = () => {
    rememberEvent();
    setContactInit({ ...base(), type: "Customer", division_id: isGlobal ? "univicoustic" : undefined,
      role_tag: "", remarks: f.website ? `Website: ${f.website}` : "" });
  };
  const asLead = () => {
    rememberEvent();
    setLeadInit({ lead_status: "New", lead_source: "Exhibition", rating: "None", division_id: "univicoustic", ...base(),
      end_client_name: f.name, end_client_phone: withDefaultCode(f.phone), end_client_email: f.email,
      location: f.city || "", remarks: [f.website && `Website: ${f.website}`, f.address && `Address: ${f.address}`, eventName.trim() && `Met at ${eventName.trim()}`].filter(Boolean).join("\n") });
  };

  const saveBatch = async () => {
    const items = queue.filter((q) => !q.dup);
    if (!items.length) { toast.error("Nothing new to save"); return; }
    if (items.some((q) => !(q.name || "").trim())) { toast.error("Every card needs a name — use ✎ to add it"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/contacts/batch", {
        contacts: items.map((q) => ({ name: q.name, designation: q.designation, company: q.company, phone: withDefaultCode(q.phone), email: q.email,
          universe_id: q.universe_id, universe_company: q.universe_company, card_scan_id: q.scan_id })),
        type: batchType, event_name: eventName.trim(), division_id: isGlobal ? "univicoustic" : undefined });
      toast.success(`Saved ${data.created.length} contact${data.created.length === 1 ? "" : "s"}${data.skipped.length ? ` · ${data.skipped.length} skipped (already in CRM)` : ""}`);
      qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contacts-count"] });
      setQueue([]); reset(); onOpenChange(false);
      navigate(eventName.trim() ? `/contacts?event=${encodeURIComponent(eventName.trim())}` : "/contacts");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const dupContacts = scan?.duplicates?.contacts || [];
  const dupLeads = scan?.duplicates?.leads || [];
  const hasDup = dupContacts.length + dupLeads.length > 0;
  const newInQueue = queue.filter((q) => !q.dup).length;

  return (
    <>
      <Dialog open={open && !contactInit && !leadInit} onOpenChange={close}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[92vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="scan-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ScanLine className="h-4 w-4 text-brand" /> Scan a visiting card / poster</DialogTitle>
            <DialogDescription>Take a photo of a card, banner or poster. We read the name, company, phone and email, then you pick Contact or Lead and fill in the rest.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label className="text-[11px] text-slate-500">Event / Exhibition (optional — remembered for the next scans)</Label>
              <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. ACETECH Mumbai 2026" className="h-9" data-testid="scan-event-name" />
            </div>
            <label className="flex items-center gap-2 h-9 text-xs text-slate-700 cursor-pointer select-none shrink-0" title="Scan card after card, then save them all as contacts in one go">
              <Switch checked={batch} onCheckedChange={toggleBatch} data-testid="scan-batch-toggle" /> <Layers className="h-3.5 w-3.5 text-slate-500" /> Batch mode
            </label>
          </div>

          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="scan-camera-input" />
          <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" onChange={onFile} data-testid="scan-gallery-input" />

          {batch ? (
            <div className="space-y-3" data-testid="scan-batch-panel">
              {busy ? (
                <div className="flex items-center gap-3 rounded-md border border-slate-200 p-2">
                  {preview && <img src={preview} alt="Card" className="h-14 w-20 object-cover rounded" />}
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-700" data-testid="scan-reading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading card {queue.length + 1}…</span>
                </div>
              ) : <CaptureButtons small={queue.length > 0} busy={busy} onCamera={() => camRef.current?.click()} onGallery={() => galRef.current?.click()} />}
              {queue.length > 0 ? (
                <>
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5" data-testid="batch-queue">
                    {queue.map((it, i) => (
                      <QueueItem key={it.scan_id} item={it} idx={i}
                        onChange={(n) => setQueue((q) => q.map((x, j) => (j === i ? n : x)))}
                        onRemove={() => setQueue((q) => q.filter((_, j) => j !== i))} />
                    ))}
                  </ul>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1 flex-1 min-w-[160px]">
                      <Label className="text-[11px] text-slate-500">Contact type for this batch</Label>
                      <Select value={batchType} onValueChange={setBatchType}>
                        <SelectTrigger className="h-9" data-testid="batch-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{(dd?.contact_type || ["Customer"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button onClick={saveBatch} disabled={saving || !newInQueue} className="h-9 gap-2 bg-slate-900 hover:bg-slate-800" data-testid="batch-save-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save all as Contacts ({newInQueue})
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-slate-400">Scan cards one after another at the stall — they pile up here. When you're done, save them all as contacts in one go.</p>
              )}
            </div>
          ) : (
            <>
              {!preview ? (
                <CaptureButtons busy={busy} onCamera={() => camRef.current?.click()} onGallery={() => galRef.current?.click()} />
              ) : (
                <div className="flex gap-3 items-start">
                  <img src={preview} alt="Card" className="h-24 w-36 object-cover rounded-md border border-slate-200 shrink-0" data-testid="scan-preview-img" />
                  <div className="text-xs text-slate-500 flex-1">
                    {busy ? <span className="inline-flex items-center gap-1.5 text-slate-700" data-testid="scan-reading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the card…</span>
                      : <>Read as <b className="text-slate-700">{scan?.fields?.kind || "image"}</b>. Check the details below and correct anything we misread.</>}
                    {!busy && <div className="mt-2"><Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={reset} data-testid="scan-retake-btn"><RotateCcw className="h-3 w-3" /> Scan another</Button></div>}
                  </div>
                </div>
              )}

              {scan && !busy && (
                <>
                  {hasDup && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs space-y-1.5" data-testid="scan-duplicates">
                      <p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Already in the CRM (same phone / email)</p>
                      {dupContacts.map((c) => (
                        <button key={c.id} type="button" onClick={() => { close(false); navigate(`/contacts?q=${encodeURIComponent(c.phone || c.email || c.name)}`); }}
                          className="w-full text-left flex items-center justify-between rounded px-2 py-1 bg-white border border-amber-100 hover:border-amber-300" data-testid={`scan-dup-contact-${c.id}`}>
                          <span><b>{c.name}</b> · Contact{c.company ? ` · ${c.company}` : ""}</span><ExternalLink className="h-3 w-3 text-amber-700" />
                        </button>
                      ))}
                      {dupLeads.map((l) => (
                        <button key={l.id} type="button" onClick={() => { close(false); navigate(`/leads/${l.id}`); }}
                          className="w-full text-left flex items-center justify-between rounded px-2 py-1 bg-white border border-amber-100 hover:border-amber-300" data-testid={`scan-dup-lead-${l.id}`}>
                          <span><b>{l.name || l.project_name}</b> · Lead · {l.company} · {l.lead_status}</span><ExternalLink className="h-3 w-3 text-amber-700" />
                        </button>
                      ))}
                      <p className="text-amber-700">Open the existing record, or continue below to create a new one anyway.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3" data-testid="scan-fields">
                    <div className="col-span-2"><F label="Person name" k="name" f={f} set={set} testid="scan-field-name" /></div>
                    <F label="Designation" k="designation" f={f} set={set} testid="scan-field-designation" />
                    <div className="space-y-1">
                      <Label className="text-[11px] text-slate-500">Company {f.universe_id && <span className="text-emerald-700 inline-flex items-center gap-0.5"><Link2 className="h-3 w-3" /> Universe</span>}</Label>
                      <UniverseLinkInput value={f.company} universeId={f.universe_id} universeCompany={f.universe_company}
                        onChange={(v) => setFields((p) => ({ ...p, company: v.value, universe_id: v.universe_id, universe_company: v.universe_company }))}
                        placeholder="Company" testid="scan-field-company" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-slate-500">Phone</Label>
                      <PhoneInput value={f.phone || ""} onChange={(v) => set("phone", v)} testid="scan-field-phone" warnNoCode />
                      {(f.phones || []).length > 1 && (
                        <div className="flex flex-wrap gap-1">{f.phones.filter((p) => p !== f.phone).map((p) => (
                          <button key={p} type="button" onClick={() => set("phone", p)} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600" data-testid="scan-alt-phone">{p}</button>
                        ))}</div>
                      )}
                    </div>
                    <F label="Email" k="email" f={f} set={set} testid="scan-field-email" type="email" />
                    <F label="Website" k="website" f={f} set={set} testid="scan-field-website" />
                    <F label="City" k="city" f={f} set={set} testid="scan-field-city" />
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <Button onClick={asContact} variant="outline" className="h-11 gap-2 border-slate-300" data-testid="scan-save-contact-btn"><UserPlus className="h-4 w-4" /> Save as Contact</Button>
                    <Button onClick={asLead} className="h-11 gap-2 bg-slate-900 hover:bg-slate-800" data-testid="scan-save-lead-btn"><Target className="h-4 w-4" /> Save as Lead</Button>
                  </div>
                  {!scan.image_stored && <p className="text-[11px] text-amber-600">Card image could not be archived; details were still read.</p>}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ContactDialog open={!!contactInit} onOpenChange={(v) => { if (!v) { setContactInit(null); close(false); } }} initial={contactInit} dd={dd} isGlobal={isGlobal}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contacts-count"] }); navigate("/contacts"); }} />
      <LeadFormDialog open={!!leadInit} onOpenChange={(v) => { if (!v) { setLeadInit(null); close(false); } }} initial={leadInit}
        onSaved={(saved) => {
          if (scan?.scan_id && saved?.id) api.post(`/card-scan/${scan.scan_id}/used`, { entity_type: "lead", entity_id: saved.id }).catch(() => {});
          qc.invalidateQueries({ queryKey: ["leads-kanban"] }); qc.invalidateQueries({ queryKey: ["leads-table"] });
          navigate(saved?.id ? `/leads/${saved.id}` : "/leads");
        }} />
    </>
  );
}

export function ScanCardButton({ compact = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!SCAN_ROLES.includes(user?.role)) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} title="Scan a visiting card or poster"
        className={compact
          ? "inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors"
          : "inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"}
        data-testid={compact ? "header-scan-card-btn" : "scan-card-btn"}>
        <ScanLine className="h-4 w-4 text-brand" />
        <span className={compact ? "hidden sm:inline" : ""}>Scan Card</span>
      </button>
      <ScanCardDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
